import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { 
  TrendingUp, 
  Mail, 
  MousePointer, 
  Eye, 
  Users,
  Calendar,
  RefreshCw
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface EmailStats {
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
}

const AdminEmailStats: React.FC = () => {
  const [stats, setStats] = useState<EmailStats>({
    totalSent: 0,
    totalOpened: 0,
    totalClicked: 0,
    openRate: 0,
    clickRate: 0,
    bounceRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  const [chartData, setChartData] = useState<any[]>([]);

  const typeData = [
    { name: 'Booking Confirmation', value: 35, color: '#0088FE' },
    { name: 'Status Change', value: 25, color: '#00C49F' },
    { name: 'Time Change', value: 15, color: '#FFBB28' },
    { name: 'Chat Message', value: 10, color: '#FF8042' },
    { name: 'Campaigns', value: 15, color: '#8884D8' }
  ];

  const deviceData = [
    { name: 'Desktop', value: 55, color: '#0088FE' },
    { name: 'Mobile', value: 35, color: '#00C49F' },
    { name: 'Tablet', value: 10, color: '#FFBB28' }
  ];

  useEffect(() => {
    fetchEmailStats();
  }, [timeRange]);

  const fetchEmailStats = async () => {
    setLoading(true);
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : timeRange === '90d' ? 90 : 365;
      startDate.setDate(endDate.getDate() - days);

      // Fetch email logs with date filter
      const { data: logs, error: logsError } = await supabase
        .from('email_logs')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .order('created_at', { ascending: false });

      if (logsError) throw logsError;

      // Fetch email opens with date filter
      const { data: opens, error: opensError } = await supabase
        .from('email_opens')
        .select('*')
        .gte('opened_at', startDate.toISOString())
        .lte('opened_at', endDate.toISOString());

      if (opensError) throw opensError;

      // Fetch email clicks with date filter
      const { data: clicks, error: clicksError } = await supabase
        .from('email_clicks')
        .select('*')
        .gte('clicked_at', startDate.toISOString())
        .lte('clicked_at', endDate.toISOString());

      if (clicksError) throw clicksError;

      // Calculate stats
      const totalSent = logs?.filter(log => log.status === 'sent').length || 0;
      const totalOpened = opens?.length || 0;
      const totalClicked = clicks?.length || 0;
      const openRate = totalSent > 0 ? (totalOpened / totalSent) * 100 : 0;
      const clickRate = totalOpened > 0 ? (totalClicked / totalOpened) * 100 : 0;
      const bounceRate = totalSent > 0 ? (logs?.filter(log => log.status === 'failed').length || 0) / totalSent * 100 : 0;

      setStats({
        totalSent,
        totalOpened,
        totalClicked,
        openRate,
        clickRate,
        bounceRate
      });

      // Generate chart data from real data
      const chartDataMap = new Map();
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      // Initialize chart data
      logs?.forEach(log => {
        const date = new Date(log.created_at);
        const monthKey = monthNames[date.getMonth()];
        
        if (!chartDataMap.has(monthKey)) {
          chartDataMap.set(monthKey, { name: monthKey, sent: 0, opened: 0, clicked: 0 });
        }
        
        if (log.status === 'sent') {
          chartDataMap.get(monthKey).sent++;
        }
      });

      // Add opens data
      opens?.forEach(open => {
        const date = new Date(open.opened_at);
        const monthKey = monthNames[date.getMonth()];
        
        if (chartDataMap.has(monthKey)) {
          chartDataMap.get(monthKey).opened++;
        }
      });

      // Add clicks data
      clicks?.forEach(click => {
        const date = new Date(click.clicked_at);
        const monthKey = monthNames[date.getMonth()];
        
        if (chartDataMap.has(monthKey)) {
          chartDataMap.get(monthKey).clicked++;
        }
      });

      const chartDataArray = Array.from(chartDataMap.values());
      setChartData(chartDataArray);

    } catch (error) {
      console.error('Error fetching email stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Sent',
      value: stats.totalSent.toLocaleString(),
      icon: <Mail className="h-4 w-4 text-blue-500" />,
      description: 'Emails delivered'
    },
    {
      title: 'Open Rate',
      value: `${stats.openRate.toFixed(1)}%`,
      icon: <Eye className="h-4 w-4 text-green-500" />,
      description: 'Emails opened'
    },
    {
      title: 'Click Rate',
      value: `${stats.clickRate.toFixed(1)}%`,
      icon: <MousePointer className="h-4 w-4 text-purple-500" />,
      description: 'Links clicked'
    },
    {
      title: 'Bounce Rate',
      value: `${stats.bounceRate.toFixed(1)}%`,
      icon: <TrendingUp className="h-4 w-4 text-red-500" />,
      description: 'Failed deliveries'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Email Analytics</h2>
          <p className="text-muted-foreground">
            Comprehensive email performance metrics and insights
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchEmailStats}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
              {stat.icon}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Performance Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart className="h-5 w-5" />
            Email Performance Over Time
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="sent" 
                stroke="#0088FE" 
                strokeWidth={2}
                name="Sent"
              />
              <Line 
                type="monotone" 
                dataKey="opened" 
                stroke="#00C49F" 
                strokeWidth={2}
                name="Opened"
              />
              <Line 
                type="monotone" 
                dataKey="clicked" 
                stroke="#FFBB28" 
                strokeWidth={2}
                name="Clicked"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Email Types Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Email Types Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={typeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {typeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Device Usage */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Device Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={deviceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {deviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Engagement Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Monthly Engagement Metrics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="sent" fill="#0088FE" name="Sent" />
              <Bar dataKey="opened" fill="#00C49F" name="Opened" />
              <Bar dataKey="clicked" fill="#FFBB28" name="Clicked" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEmailStats;